import { debounceTime, tap, map } from 'rxjs/operators';
import { rxInitSubjectCallback, useObservable } from 'src/plugins/rxjs/base';
import { defineComponent, onMounted, onUnmounted, ref, computed, watch, inject, } from 'vue';
import { imageSizes, generateSrcString } from 'src/plugins/images/generateSrcString';
import { alternativeImgPlugin } from 'src/plugins/images/alternative';
import { checkAndReturnUrl, checkUrlObjAndReturnUrl, convertUrlToWebp, } from 'src/plugins/images/general';
import { AppGeneralVariables } from 'src/plugins/general/variables';
import cloneDeep from 'lodash.clonedeep';
import picSet from './picSet.vue';
export default defineComponent({
    props: {
        picClass: {
            type: Object,
            default: () => {
                return {};
            },
        },
        picStyle: {
            type: Object,
            default: () => {
                return {};
            },
        },
        disable: Boolean,
        url: [String, Object],
        blurUrl: [String, Object],
        format: String,
        alternative: {
            type: String,
            default: 'https://dminqct125wnz.cloudfront.net/common/default/listing/1500_spaces_default2.jpeg',
        },
        appType: {
            type: String,
            validator: (value) => {
                return ['getspaces'].indexOf(value) !== -1;
            },
        },
        fullWidth: Boolean,
        sizeType: String,
        originalSize: String,
        componentSize: String,
        containerClass: {
            type: Object,
            default: () => {
                return {};
            },
        },
        customWidth: Number,
        customHeight: Number,
        draggable: {
            type: String,
            default: 'true',
        },
        allowOtherFormats: Boolean,
        blurInitial: Boolean,
        alt: String,
        height: String,
        centered: Boolean,
        showLoad: Boolean,
        side: Boolean,
        end: Boolean,
        forceRatio: Boolean,
        ratio: Number,
        routerWrap: Boolean,
        fullHeight: Boolean,
        relative: Boolean,
        preloaded: Boolean,
        pathName: String,
        pathParams: {
            type: Object,
            default: () => {
                return {};
            },
        },
        pathQueries: {
            type: Object,
            default: () => {
                return {};
            },
        },
        target: {
            type: String,
            default: '_self',
        },
        targetApp: {
            type: String,
            default: 'getspaces',
        },
    },
    emits: ['click', 'load'],
    components: {
        picSet,
    },
    setup(props, context) {
        const routeDisabled = inject('routeDisabled', ref(false));
        const { appMode } = AppGeneralVariables();
        /*****
         * Image style
         *****/
        const parsedContainerClass = computed(() => {
            return {
                ...props.containerClass,
                'justify-center': !props.side,
                'justify-end': props.side && props.end,
                'full-width': props.forceRatio || props.fullWidth,
                'full-height': props.fullHeight,
                'self-center': props.centered,
            };
        });
        const containerStyle = computed(() => {
            return {
                ...(props.forceRatio
                    ? {
                        'padding-top': `${props.ratio * 100}%`,
                    }
                    : {}),
                ...(props.relative
                    ? {
                        position: 'relative',
                    }
                    : {}),
            };
        });
        /*****
         * Urls
         ****/
        const { alternativeWebpSrcSet, alternativeSrcSet } = alternativeImgPlugin();
        // size that results in a blurred url
        const blurImgUrl = computed(() => {
            return checkUrlObjAndReturnUrl(props.blurUrl, props.format);
        });
        // single url
        const imgUrl = computed(() => {
            return checkUrlObjAndReturnUrl(props.url, props.format);
        });
        // generate src sets
        const srcSets = computed(() => {
            if (!props.sizeType || !props.originalSize || !imageSizes[props.sizeType]) {
                return {
                    original: '',
                    webp: '',
                };
            }
            return generateSrcString({
                url: checkAndReturnUrl(intersected.value ? imgUrl.value : srcImage.value, props.format),
                originalSize: props.originalSize,
                sizeType: props.sizeType,
            });
        });
        // webp url
        const webp = computed(() => {
            return convertUrlToWebp(intersected.value ? imgUrl.value : srcImage.value || '');
        });
        // original url
        const original = computed(() => {
            if (intersected.value) {
                return checkAndReturnUrl(imgUrl.value, props.format);
            }
            else if (srcImage.value) {
                return checkAndReturnUrl(srcImage.value, props.format);
            }
            return '';
        });
        /*****
         * Emit images loaded
         *****/
        const loaded = ref(props.preloaded);
        const { subject: debounceEmit$ } = rxInitSubjectCallback();
        function loadEmitter(evt) {
            console.log('loadEmitter', evt);
            loaded.value = true;
            debounceEmit$.next(evt);
        }
        useObservable(debounceEmit$.pipe(debounceTime(500), map((v) => cloneDeep(v)), tap((evt) => {
            context.emit('load', evt);
        })));
        /*****
         * Observe intersection
         *****/
        const srcImage = ref('');
        const observer = ref(null);
        const intersected = ref(props.preloaded);
        // load src images on init
        if (props.preloaded) {
            srcImage.value = imgUrl.value;
        }
        watch(imgUrl, (newValue, oldValue) => {
            if (oldValue === '' && intersected.value) {
                srcImage.value = newValue;
            }
        });
        // observer
        const root = ref(null);
        onMounted(() => {
            if (!props.preloaded && ('IntersectionObserver' in window || props.disable)) {
                observer.value = new IntersectionObserver((entries) => {
                    const image = entries[0];
                    if (image.isIntersecting && observer.value) {
                        observer.value.disconnect();
                        intersected.value = true;
                        srcImage.value = imgUrl.value;
                    }
                    else if (props.blurInitial) {
                        srcImage.value = blurImgUrl.value;
                    }
                    else {
                        srcImage.value = '';
                    }
                });
                observer.value.observe(root.value);
            }
            else {
                srcImage.value = imgUrl.value;
                intersected.value = true;
            }
        });
        onUnmounted(() => {
            observer.value?.disconnect();
        });
        return {
            appMode,
            routeDisabled,
            parsedContainerClass,
            containerStyle,
            alternativeWebpSrcSet,
            alternativeSrcSet,
            original,
            srcSets,
            webp,
            loaded,
            loadEmitter,
            intersected,
            observer,
            srcImage,
            root,
        };
    },
});
